import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, Input, ModalUI, Selector, usePortal } from 'sonata-react-components';

import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { ConcentricLocationsModalStyled } from './ConcentricLocationsModal.style';

import type { ThemeType } from 'sonata-react-components';

type Props = {
  isModalOpened: boolean;
  itemIds: string[];
  onModalClose: () => void;
  onModalSave: () => void;
  theme?: ThemeType;
};

type State = {
  amount?: number;
  radiusList?: number[];
};

export default function ConcentricLocationsModal({
  isModalOpened,
  itemIds,
  onModalClose,
  onModalSave,
  theme = 'light'
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<any>(null);

  const { setConcentricLocations } = useMultiFlightMap();

  const [{ amount, radiusList }, setState] = useState<State>({});

  const targetBody = usePortal('concentricLocationsModal');

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const handleAmountChange = (value: { id: string }) => {
    const newAmount = parseInt(value.id, 10);
    setState(prevState => ({
      ...prevState,
      amount: newAmount,
      radiusList: Array.from({ length: newAmount }, (_, index) => (index + 1) * 50)
    }));
  };

  const handleRadiusChange = (index: number, value: string) => {
    const newRadiusList = [...(radiusList || [])];
    newRadiusList[index] = parseInt(value, 10);
    setState(prevState => ({
      ...prevState,
      radiusList: newRadiusList
    }));
  };

  const handleOnSave = () => {
    setConcentricLocations(itemIds, radiusList || []);
    onModalSave();
  };

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={theme}>
      <ModalUI
        centerButton={
          <ButtonUI
            disabled={!amount || !radiusList || !radiusList.every(r => r && r > 0)}
            onClick={handleOnSave}
            text={formatMessage({
              defaultMessage: 'Save',
              id: 'common.save'
            })}
          />
        }
        closeModalCallback={onModalClose}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Select Concentric Locations',
          id: 'concentricLocationsModal.title'
        })}
        width="520px"
      >
        <ConcentricLocationsModalStyled className="concentricLocationsModal">
          <Selector
            className="concentricLocationsModal__numLocationsSelector"
            id="spc-concentricLocationsModal-numLocationsSelector"
            label={formatMessage({
              defaultMessage: 'Num. of locations',
              id: 'concentricLocationsModal.numLocationsLabel'
            })}
            onChange={handleAmountChange}
            options={Array.from({ length: 5 }, (_, index) => ({
              id: (index + 1).toString(),
              label: formatMessage(
                {
                  defaultMessage: '{index} locations',
                  id: 'concentricLocationsModal.numLocationsOption'
                },
                { index: index + 1 }
              )
            }))}
            required
            type="simple"
          />
          {amount != null && radiusList?.length && (
            <>
              <div className="concentricLocationsModal__separator" />
              <div className="concentricLocationsModal__radiusList">
                {radiusList!.map((_, index) => (
                  <Input
                    className="concentricLocationsModal__radiusInput"
                    id={`spc-concentricLocationsModal-radius${index}Input`}
                    key={index}
                    label={formatMessage(
                      {
                        defaultMessage: 'Radius {index} (meters)',
                        id: 'concentricLocationsModal.radius'
                      },
                      {
                        index: index + 1
                      }
                    )}
                    name={`radius-${index}`}
                    onChange={value => handleRadiusChange(index, value as string)}
                    shape="squareUI"
                    type="number"
                    value={radiusList ? radiusList[index] || '' : ''}
                  />
                ))}
              </div>
            </>
          )}
        </ConcentricLocationsModalStyled>
      </ModalUI>
    </CustomThemeProvider>,
    targetBody
  );
}
